import React, { useState, useEffect } from 'react';
import { compose } from 'ramda';
import { canUseDOM } from 'exenv';

import { withEditMode, withEditModeNotice } from '_containers/BaseComponent';
import { Grid, Link } from '_utils/components/Corporate';
import { mq } from '_utils/styles';
import HeadingWithDescription from '_utils/components/Corporate/HeadingWithDescription';

import ContentListItem from './ContentListItem';
import ItemsWrapper from './ItemsWrapper';

import { CardThemes, GroupedContentListProps } from './definitions';
import { Section, EditorDiv, FootLinkRow, GridItem } from './StyledGroupedContentList';
import { BackgroundColors } from '_utils/components/Corporate/Section';

const EditorText = compose(withEditModeNotice)(EditorDiv);

let enquire;

if (canUseDOM) {
  enquire = require('enquire.js');
}

const isValidLink = (d) => d?.value?.href !== '';

const formatChild = (
  editMode,
  { id, fields: { title, description, footerLink, icon, ...originalLinks } }
) => {
  let links = [];

  // Get links in the right order
  for (let i = 1; i <= 10; i++) {
    if (typeof originalLinks[`link${i}`] !== 'undefined') {
      links.push(originalLinks[`link${i}`]);
    }
  }

  if (!editMode) {
    links = links.filter(isValidLink);
  }

  return {
    id,
    title,
    description,
    footerLink,
    icon,
    links
  };
};

const isValidChild = ({ links }) => links.length > 0;

const populateThemesArray = () => Object.keys(CardThemes);

const GroupedContentList: React.FC<GroupedContentListProps> = ({
  fields,
  fields: { children, description, footerLink, heading, headingLevel },
  editMode,
  params
}) => {
  let themes = [];

  // Default
  let backgroundColor = BackgroundColors.White;

  try {
    backgroundColor = BackgroundColors[params.backgroundColour];
  } catch {}

  const getCardThemes = () => {
    if (themes.length === 0) {
      themes = populateThemesArray();
    }

    return themes.shift();
  };

  const mediaQuery = `screen and ${mq.lessThan('lg', false)}`;
  const [carouselEnabled, setCarouselEnabled] = useState(
    canUseDOM ? window.matchMedia(mediaQuery).matches : true
  );
  useEffect(() => {
    if (enquire) {
      enquire.register(mediaQuery, {
        match: () => setCarouselEnabled(true),
        unmatch: () => setCarouselEnabled(false)
      });
    }
    return () => enquire.unregister(mediaQuery);
  });

  const level = parseInt(headingLevel?.value?.toLowerCase().replace('h', '')) || 2;

  const spacingUnit = level === 1 ? 7 : 5;

  const items =
    (children && children.map(formatChild.bind(null, editMode)).filter(isValidChild)) || [];

  if (!editMode && items.length === 0) {
    return null;
  }

  const footLink = (
    <FootLinkRow $config={{ gutters: ['left', 'right'] }} $mt={{ xs: 1, md: 0 }}>
      <Grid.Item $config={{ col: { xs: 12 }, gutters: ['left', 'right'] }}>
        <Link field={footerLink} description={footerLink?.value?.text} />
      </Grid.Item>
    </FootLinkRow>
  );

  const missingDatasource = Object.keys(fields).length === 0;
  const missingItems = items.length === 0;

  if (editMode && missingDatasource) {
    return (
      <EditorText noticeEnabled={true} noticeMessage="Grouped Content List has no data source" />
    );
  }

  return (
    <Section className={backgroundColor} $sectionTheme={backgroundColor}>
      <Grid.Container $py={spacingUnit}>
        <Grid.Row $config={{ gutters: ['left', 'right'] }}>
          <Grid.Item $config={{ col: { xs: 12 }, gutters: ['left', 'right'] }}>
            <HeadingWithDescription
              headingField={heading}
              descriptionField={description}
              headingLevel={level}
            />
          </Grid.Item>
        </Grid.Row>
        {editMode && (
          <EditorText
            noticeEnabled={missingItems}
            noticeMessage="No items associated, please add an item for component to display."
          />
        )}
        <ItemsWrapper carouselEnabled={carouselEnabled}>
          {items.map(({ id, icon, title, description, footerLink, links }) => {
            let item = (
              <ContentListItem
                key={id}
                icon={icon}
                title={title}
                description={description}
                links={links}
                footerLink={footerLink}
                cardTheme={getCardThemes()}
                headingLevel={level + 1}
                editMode={editMode}
              />
            );

            if (editMode) {
              item = (
                <EditorText
                  noticeEnabled={links.filter(isValidLink).length === 0}
                  noticeLevel={'warning'}
                  noticeMessage={`Please configure at least 1 link for this card to display`}
                >
                  {item}
                </EditorText>
              );
            }

            if (carouselEnabled) {
              return item;
            }

            return (
              <GridItem
                key={id}
                $display="flex"
                $flexDirection="column"
                $config={{ col: { md: 4 }, gutters: ['left', 'right'] }}
                $mb="4"
              >
                {item}
              </GridItem>
            );
          })}
        </ItemsWrapper>
        {editMode ? footLink : Boolean(footerLink?.value?.href) && footLink}
      </Grid.Container>
    </Section>
  );
};

export default compose(withEditMode)(GroupedContentList);
